import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import * as moment from 'moment';
import { LanguageService } from '../../../../navigation/language.service';
import { Subscription } from 'rxjs';

interface CellValue {
  value: number;
  date: Date;
  color: string;
}

@Component({
  selector: 'app-hours-table',
  templateUrl: './hours-table.component.html',
  styleUrls: ['./hours-table.component.scss']
})
export class HoursTableComponent implements OnInit, OnDestroy {
  dataSource: MatTableDataSource<any>;
  displayedColumns: string[] = ['regions']
  tableData: {region: string; values: CellValue[]; valueMap: {[key: string]: CellValue}}[] = [];
  monthHeaders: { month: string; dateKey: any; span: number }[] = [];
  rowWidth: number;
  langSubscription: Subscription;

  @Input() group;
  @Input() groupId: string;
  @Input() filterCache;

  constructor(
    private languageService: LanguageService,
  ) { }

  ngOnInit(): void {
    this.prepareTableData();
    setTimeout(() => {
      this.separateMonthSections();
      this.rowWidth = this.getFullWidth();
    }, 0);
    this.langSubscription = this.languageService.onLanguageChange.subscribe(v => {
      if (this.monthHeaders.length) {
        this.monthHeaders?.map(value => {
          value.month = moment(value.dateKey).locale(v).format('MMMM YYYY');
        });
      }
    })
  }

  ngOnDestroy(): void {
    this.langSubscription.unsubscribe();
  }

  prepareTableData(): void {
    this.group.metricvalueSet.edges.forEach(value => {
      const regionName = value?.node?.regionGroup?.title;
      const existing = this.tableData.find(item => item.region === regionName);

      const dateKey = moment(value.node.date).format('YYYY-MM-DD');
      const entry = {
        value: value.node.value,
        date: value.node.date,
        color: this.getColorByValue(value.node.value)
      };

      if (!existing) {
        this.tableData.push({
          region: regionName,
          values: [entry],
          valueMap: { [dateKey]: entry }
        });
      } else {
        existing.values.push(entry);
        existing.valueMap[dateKey] = entry;
      }
    });

    if (this.tableData.length) {
      const summaryRow = this.tableData.find(item => !item.region);
      if (summaryRow) {
        this.tableData = this.tableData.filter(item => item.region);
        summaryRow.region = this.group.title;
        this.tableData.unshift(summaryRow);
      }
    }

    this.prepareColumns();
    this.dataSource = new MatTableDataSource(this.tableData);
  }

  getColumnValue(element, column: string) {
    const dateKey = column.replace('hours-', '');
    return element.valueMap?.[dateKey] || null;
  }

  prepareColumns(): void {
    this.monthHeaders = [];

    let current = moment(this.filterCache.dateRange.dateFrom).locale(this.languageService.getLanguage());
    const end = moment(this.filterCache.dateRange.dateTo).locale(this.languageService.getLanguage());

    let currentMonth = current.format('MMMM YYYY');
    let span = 0;

    while (current.isSameOrBefore(end)) {
      const dateKey = current.format('YYYY-MM-DD');
      this.displayedColumns.push(`hours-${dateKey}`);

      if (current.format('MMMM YYYY') === currentMonth) {
        span++;
      } else {
        this.monthHeaders.push({ month: currentMonth, dateKey: moment(current).subtract(1, 'day').format('YYYY-MM-DD'), span });
        currentMonth = current.format('MMMM YYYY');
        span = 1;
      }

      current.add(1, 'day');
    }

    this.monthHeaders.push({ month: currentMonth, dateKey: moment(current).subtract(1, 'day').format('YYYY-MM-DD'), span });
  }

  getColorByValue(value: number): string {
    if (value > 0 && value <= 5) {
      return '#F8696BCC';
    } else if (value <= 12) {
      return '#FB9A75CC';
    } else if (value <= 18) {
      return '#FCBC7BB2';
    } else if (value <= 20) {
      return '#FEDB81B2';
    } else if (value <= 25) {
      return '#EFE68380';
    } else if (value <= 38) {
      return '#C5DA8180';
    } else if (value < 40) {
      return '#9CCE7E4D';
    } else if (value >= 40) {
      return '#63BE7B4D';
    }
  }

  separateMonthSections() {
    if (this.monthHeaders.length === 1 && this.displayedColumns[1].split('-')[3] !== '01') {
      Array.from(document.querySelectorAll('.cdk-column-regions')).forEach(el => {
        el.classList.add('separated-regions');
      });
      return;
    }
    this.displayedColumns.map((v, i) => {
      if (v.split('-')[3] === '01' || i === 1) {
        Array.from(document.querySelectorAll('.cdk-column-' + v)).forEach(el => {
          el.classList.add('separated');
        });
      }
    });
  }

  getFullWidth() {
    return this.monthHeaders.map((v) => v.span).reduce((prev, curr) => prev + curr, 0);
  }

  protected readonly moment = moment;
}
